import {useMemo} from "react";

import {WorkspaceTour, type WorkspaceTourStep} from "./WorkspaceTour";

type DashboardTabKey = "ceo_summary" | "xero_pnl" | "cashflow_summary" | "bs_summary";

interface DashboardTourProps {
    open: boolean;
    activeTab: string;
    visibleTabs: string[];
    onClose: () => void;
    onTabChange: (tab: DashboardTabKey) => void;
}

const TOUR_STEPS: WorkspaceTourStep[] = [
    {
        id: "dashboard-overview",
        selector: '[data-tour="dashboard-title"]',
        extraSelectors: ['[data-tour="dashboard-tab-bar"]'],
        title: "This dashboard is the high-level summary layer",
        body: "Everything here stays on the same reporting window. Move across these tabs to shift from executive summary to the statement summaries at a glance.",
        badge: "Overview",
        placement: "bottom",
    },
    {
        id: "ceo-tab",
        tab: "ceo_summary",
        selector: '[data-tour="dashboard-tab-trigger-ceo_summary"]',
        title: "CEO Summary is the morning glance",
        body: "Start here for the executive verification view after a sync or forecast change, then drill into the statement summaries if something needs more detail.",
        badge: "CEO Summary",
        placement: "bottom",
    },
    {
        id: "pnl-tab",
        tab: "xero_pnl",
        selector: '[data-tour="dashboard-tab-trigger-xero_pnl"]',
        title: "P&L Summary is the income statement view",
        body: "Use this tab when you want revenue, gross profit, operating expenses, and net profit for the selected period, with actuals and comparisons at a glance.",
        badge: "P&L Summary",
        placement: "bottom",
    },
    {
        id: "cashflow-tab",
        tab: "cashflow_summary",
        selector: '[data-tour="dashboard-tab-trigger-cashflow_summary"]',
        title: "Cash Flow Summary answers the cash question",
        body: "This tab gives the high-level cash view: current position, movement, and runway. Use it when you want liquidity at a glance without opening the detailed explorer.",
        badge: "Cash Flow",
        placement: "bottom",
    },
    {
        id: "bs-tab",
        tab: "bs_summary",
        selector: '[data-tour="dashboard-tab-trigger-bs_summary"]',
        title: "Balance Sheet Summary closes the loop",
        body: "Use this tab to review assets, liabilities, equity, and balance sheet health for the same reporting window as the rest of the dashboard.",
        badge: "Balance Sheet",
        placement: "bottom",
    },
    {
        id: "agent-panel",
        selector: '[data-tour="agent-panel"]',
        requiresAgentPanel: true,
        title: "The left agent panel stays in the workflow",
        body: "This side panel keeps the agent in context from the tab you are on down to the prompt box, so you can move from summary review into analysis or approved model updates without leaving the page.",
        badge: "Agent Panel",
        placement: "bottom",
    },
];

export function DashboardTour({open, activeTab, visibleTabs, onClose, onTabChange}: DashboardTourProps) {
    const steps = useMemo(
        () => TOUR_STEPS.filter((step) => !step.tab || visibleTabs.includes(step.tab)),
        [visibleTabs],
    );

    return (
        <WorkspaceTour
            open={open}
            activeTab={activeTab}
            onClose={onClose}
            onTabChange={(tab) => onTabChange(tab as DashboardTabKey)}
            steps={steps}
            dialogLabel="Dashboard walkthrough"
        />
    );
}
